import React, { useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  Form,
  FormGroup,
  Label,
} from 'reactstrap';
import { TextField } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { AIButton } from 'components/AI/AIButton/AIButton';
import ConfirmationModal from 'common/ConfirmationModal';
import { GeneratedContentDisplay, AILoadingState } from './AISharedComponents';

// Form validation schema
const composeSchema = yup.object().shape({
  prompt: yup
    .string()
    .trim()
    .required('Please enter a prompt to generate content'),
});

const replySchema = yup.object().shape({
  message: yup.string().trim().optional(), // Message is now optional for reply mode
});

const AIModal = ({
  isOpen,
  onToggle,
  onUseContent,
  generateAiContent, // For compose mode
  generateAiReply, // For reply mode
  isAiEnabled,
  currentContent = '',
  threadId = '',
  mode = 'compose', // 'compose' or 'reply'
}) => {
  const [generatedContent, setGeneratedContent] = useState('');
  const [generatedSubject, setGeneratedSubject] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Use different schemas based on mode
  const schema = mode === 'compose' ? composeSchema : replySchema;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: mode === 'compose' ? { prompt: '' } : { message: '' },
    mode: 'onChange', // Enable validation on change
  });

  const onSubmit = async (data) => {
    if (mode === 'reply' && !threadId) {
      setError('message', {
        type: 'manual',
        message: 'Thread ID is required to generate a reply',
      });
      return;
    }

    setIsGenerating(true);
    clearErrors();

    try {
      let content;
      let subject;
      if (mode === 'compose') {
        const { content: aiGeneratedContent, subject: aiGeneratedSubject } =
          await generateAiContent(data.prompt);
        content = aiGeneratedContent;
        subject = aiGeneratedSubject;
      } else {
        const request = {
          thread_id: threadId,
          message: data.message ? data.message.trim() : '', // Handle empty message
        };
        content = await generateAiReply(request);
      }

      setGeneratedContent(content);
      setGeneratedSubject(subject);
      setHasGenerated(true);
    } catch (err) {
      // Extract the actual error message from the backend
      const errorMessage =
        err?.message ||
        `Failed to generate ${
          mode === 'compose' ? 'content' : 'reply'
        }. Please try again.`;

      setError(mode === 'compose' ? 'prompt' : 'message', {
        type: 'manual',
        message: errorMessage,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerate = () => {
    handleSubmit(onSubmit)();
  };

  const handleUseContent = () => {
    if (generatedContent) {
      // Check if there's existing content
      if (currentContent && currentContent.trim()) {
        setShowConfirmation(true);
      } else {
        // No existing content, use AI content directly
        onUseContent(generatedContent, generatedSubject);
        handleClose();
      }
    }
  };

  const handleConfirmReplace = () => {
    onUseContent(generatedContent, generatedSubject);
    setShowConfirmation(false);
    handleClose();
  };

  const handleCancelReplace = () => {
    setShowConfirmation(false);
  };

  const handleClose = () => {
    reset(mode === 'compose' ? { prompt: '' } : { message: '' });
    setGeneratedContent('');
    setGeneratedSubject('');
    setHasGenerated(false);
    setIsGenerating(false);
    clearErrors();
    setShowConfirmation(false);
    onToggle();
  };

  if (!isAiEnabled) {
    return null;
  }

  const isComposeMode = mode === 'compose';
  const modalTitle = isComposeMode
    ? 'Generate Message with AI'
    : 'Generate AI Reply';
  const generateButtonText = isComposeMode ? 'Generate' : 'Generate Reply';
  const useButtonText = isComposeMode ? 'Use This' : 'Use This Reply';
  const loadingMessage = isComposeMode
    ? 'Generating your message...'
    : 'Analyzing conversation and generating reply...';
  const contentLabel = isComposeMode ? 'Generated Content' : 'Generated Reply';

  return (
    <>
      <Modal
        size="lg"
        isOpen={isOpen}
        toggle={handleClose}
        className={`ai-modal ai-${mode}-modal`}
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader toggle={handleClose}>
          <div className="d-flex align-items-center">
            <span className="mr-2">✨</span>
            {modalTitle}
          </div>
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit(onSubmit)}>
            {isComposeMode ? (
              <FormGroup>
                <Label for="ai-prompt">
                  What would you like to write about?
                  <span className="text-muted ml-2">
                    (Be specific for better results)
                  </span>
                </Label>
                <TextField
                  id="ai-prompt"
                  multiline
                  rows={4}
                  fullWidth
                  variant="outlined"
                  {...register('prompt')}
                  placeholder="Example: Write a professional follow-up message about the quarterly tax documents that are due next week..."
                  disabled={isGenerating}
                  error={Boolean(errors.prompt)}
                  helperText={errors.prompt?.message}
                />
              </FormGroup>
            ) : (
              <FormGroup>
                <Label for="ai-message">
                  What would you like to reply about?
                  <span className="text-muted ml-2">
                    (Optional - leave blank to generate based on conversation
                    history)
                  </span>
                </Label>
                <TextField
                  id="ai-message"
                  multiline
                  rows={3}
                  fullWidth
                  variant="outlined"
                  {...register('message')}
                  placeholder="Optional: Add specific context or instructions for the reply."
                  disabled={isGenerating}
                  error={Boolean(errors.message)}
                  helperText={errors.message?.message}
                />
              </FormGroup>
            )}

            {/* Generated Content Display */}
            {hasGenerated && generatedContent && (
              <GeneratedContentDisplay
                content={generatedContent}
                label={contentLabel}
              />
            )}

            {/* Loading State */}
            {isGenerating && <AILoadingState message={loadingMessage} />}
          </Form>
        </ModalBody>

        <div className="modal-footer">
          <div className="d-flex justify-content-between w-100">
            <Button
              color="secondary"
              onClick={handleClose}
              disabled={isGenerating}
            >
              Cancel
            </Button>

            <div className="d-flex gap-2">
              {!hasGenerated ? (
                <AIButton
                  text={generateButtonText}
                  variant="contained"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isGenerating}
                />
              ) : (
                <>
                  <AIButton
                    text="Regenerate"
                    variant="text"
                    onClick={handleRegenerate}
                    disabled={isGenerating}
                  />
                  <AIButton
                    text={useButtonText}
                    variant="contained"
                    onClick={handleUseContent}
                    disabled={isGenerating || !generatedContent}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmation}
        className="customModal customModal--w500 customModal--nopadding"
        messageText={
          <div>
            <p>
              You already have content in your{' '}
              {isComposeMode ? 'message' : 'reply'}. Would you like to replace
              it with the AI-generated {isComposeMode ? 'content' : 'reply'}?
            </p>
            <div
              className="border rounded p-3 bg-light mt-3"
              style={{ maxHeight: '200px', overflowY: 'auto' }}
            >
              <strong>
                AI Generated {isComposeMode ? 'Content' : 'Reply'}:
              </strong>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: '10px' }}>
                {generatedContent}
              </div>
            </div>
          </div>
        }
        noBtnClick={handleCancelReplace}
        yesBtnClick={handleConfirmReplace}
        yesBtnText="Replace"
        noBtnText="Cancel"
      />
    </>
  );
};

export default AIModal;
